import React from 'react';
import { observer } from 'mobx-react-lite';
import Dropzone from 'react-dropzone';
import Tooltip from '@material-ui/core/Tooltip';
import { confirmAlert } from 'react-confirm-alert';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './DropzoneWrapper.module.css';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import commonStyles from '../../../../assets/stylesheets/common.module.css';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const DropzoneWrapper = observer(function DropzoneWrapper(props) {
  const { commonStore } = useRootStore();
  const { t } = useTranslation();

  const { displayUploadButton, isSMSChannel, isWebChatChannel } = props;

  const disableDropzoneClick = React.useCallback((event) => {
    event.preventDefault();
  }, []);

  const resetData = React.useCallback(() => {
    commonStore.setIsUploading(false);
    commonStore.setDragAndDropChildElementCounter(0);
    commonStore.setFullModeChatFileUpload(null);
  }, [commonStore]);

  const addFilesToPreview = (accepted, rejected) => {
    if (rejected && rejected.length > 0) {
      if (rejected.some((f) => f?.file?.size > 5000000)) {
        const message = `${t('DropzoneWrapper.FileSizeLimitExceeded')} 

        ${rejected.map(
          (m) =>
            `${t('DropzoneWrapper.LargeFilesNameAndSizeList', {
              fileName: m?.file?.name,
              fileSize: (m?.file?.size / 1000000).toFixed(2),
            })}`
        )}`;

        confirmAlert({
          customUI: ({ onClose }) => (
            <CustomConfirmAlert
              message={message}
              falseText={t('Common.Close')}
              falseEvent={onClose}
            />
          ),
        });
      } else if (rejected.some((f) => !f?.file?.type.startsWith('image'))) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <CustomConfirmAlert
              message={t('DropzoneWrapper.FilesAreNotImagesWarning')}
              falseText={t('Common.Close')}
              falseEvent={onClose}
            />
          ),
        });
      }

      resetData();
      return;
    }

    commonStore.setFullModeChatFileUpload(accepted);
  };

  const uploadingText = commonStore.isUploading ? (
    <React.Fragment>
      <span>{t('DropzoneWrapper.Uploading')}</span>

      <div className={[commonStyles.spinner, styles.bouncingDots].join(' ')}>
        <div className={commonStyles.bounce1} />

        <div className={commonStyles.bounce2} />

        <div className={commonStyles.bounce3} />
      </div>
    </React.Fragment>
  ) : (
    t('DropzoneWrapper.DropImagesInfoMessage')
  );

  const uploadButtonStyles = [styles.uploadButtonFullMode];
  if (isSMSChannel) {
    uploadButtonStyles.push(styles.uploadButtonGreen);
  } else if (isWebChatChannel) {
    uploadButtonStyles.push(styles.uploadButtonBlue);
  }

  return displayUploadButton ? (
    <Dropzone
      onClick={disableDropzoneClick}
      onDrop={addFilesToPreview}
      multiple={false}
      maxSize={5000000}
      accept="image/*"
    >
      {({ getRootProps, getInputProps }) => (
        <Tooltip
          PopperProps={{ disablePortal: true }}
          arrow
          enterDelay={1000}
          title={t('DropzoneWrapper.UploadFile')}
        >
          <div
            {...getRootProps({
              className: uploadButtonStyles.join(' '),
            })}
          >
            <input {...getInputProps()} data-testid="uploadFilesBtn" />
          </div>
        </Tooltip>
      )}
    </Dropzone>
  ) : (
    <div hidden={commonStore.dragAndDropChildElementCounter === 0}>
      <Dropzone
        onClick={disableDropzoneClick}
        onDrop={addFilesToPreview}
        multiple={false}
        maxSize={5000000}
        accept="image/*"
      >
        {({ getRootProps }) => (
          <div
            {...getRootProps({
              className: styles.dropZone,
            })}
          >
            <h2>{uploadingText}</h2>
          </div>
        )}
      </Dropzone>
    </div>
  );
});
DropzoneWrapper.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  channel: PropTypes.object,
  displayUploadButton: PropTypes.bool,
  chatMode: PropTypes.string,
};
DropzoneWrapper.whyDidYouRender = true;
DropzoneWrapper.displayName = 'DropzoneWrapper';

export default DropzoneWrapper;
